import React, { useState, useEffect, useCallback, useRef } from "react";
import { View, StyleSheet } from "react-native";
import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";
import MessagesContainer from "./MessagesContainer";
import { useGetCompanionChatByIdQuery } from "../../graphql/generated/graphql";
import useCompanionLlmStream from "../../hooks/useCompanionLlmStream";
import { v4 as uuidv4 } from "uuid";

interface ChatWindowContainerProps {
  chatId: string | null;
  onChatIdChange?: (chatId: string) => void;
}

interface Message {
  sender: string;
  content: string;
  created?: string;
  id: string;
}

const ChatWindowContainer = ({ chatId, onChatIdChange }: ChatWindowContainerProps) => {
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<string | null>(null);
  const [resetAutoScroll, setResetAutoScroll] = useState(0);

  const { data, loading } = useGetCompanionChatByIdQuery({
    skip: !chatId,
    fetchPolicy: "network-only",
    variables: {
      id: chatId!,
    },
  });

  const { startStream, cancelStream, isStreaming } = useCompanionLlmStream();

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      return;
    }
    const conversationMessages: Message[] = (data?.getCompanionChatById?.conversation ?? [])
      .map((msg) => {
        const content = msg.content || "";
        const sender = msg.sender || "ai";

        return {
          id: msg.id,
          sender,
          content,
          created: msg.created || undefined,
        };
      })
      .reverse(); // Reverse to show oldest first

    setMessages(conversationMessages);
  }, [data, chatId]);

  const appendChunkToMessage = useCallback(
    ({ text, messageId }: { text: string; messageId?: string }) => {
      if (!text) {
        return;
      }

      // Set the messageId from backend on first chunk if not already set
      if (messageId && !currentStreamingMessageId) {
        setCurrentStreamingMessageId(messageId);
      }

      const targetId = messageId || currentStreamingMessageId;

      if (!targetId) {
        return;
      }

      setMessages((prev) => {
        let found = false;
        const updated = prev.map((message) => {
          if (message.id !== targetId) {
            return message;
          }
          found = true;
          return {
            ...message,
            content: `${message.content}${text}`,
          };
        });

        if (!found) {
          return [
            ...prev,
            {
              id: targetId,
              sender: "ai",
              content: text,
            },
          ];
        }

        return updated;
      });
    },
    [currentStreamingMessageId]
  );

  const handleStreamComplete = useCallback(
    (payload?: { event?: { chatId?: string; messageId?: string }; text: string }) => {
      const finalText = payload?.text;
      const completionEvent = payload?.event;

      if (completionEvent?.chatId && !chatId && onChatIdChange) {
        onChatIdChange(completionEvent.chatId);
      }

      if (finalText) {
        const finalId = completionEvent?.messageId;
        const streamingId = currentStreamingMessageId;
        if (finalId && streamingId) {
          setMessages((prev) =>
            prev.map((message) => {
              // Only update the streaming message if we have a finalId
              if (message.id === streamingId) {
                return {
                  ...message,
                  id: finalId,
                  sender: "ai",
                  content: finalText,
                };
              }
              return message;
            })
          );
        }
        setCurrentStreamingMessageId(null);
      } else {
        setCurrentStreamingMessageId(null);
      }
    },
    [chatId, onChatIdChange, currentStreamingMessageId]
  );

  const handleStreamError = useCallback(
    ({ error }: { error: Error }) => {
      console.error("[ChatWindowContainer] Stream error", { message: error.message });
      const targetId = currentStreamingMessageId;
      if (targetId) {
        setMessages((prev) => prev.filter((message) => message.id !== targetId));
      }
      setCurrentStreamingMessageId(null);
    },
    [currentStreamingMessageId]
  );

  const handleSubmit = useCallback(async () => {
    if (!inputValue.trim() || isStreaming) {
      return;
    }

    const trimmedInput = inputValue.trim();
    const userMessage: Message = {
      id: uuidv4(), // just for the sake of having an id in a message array
      sender: "human",
      content: trimmedInput,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    // Reset streaming message ID - it will be set from backend on first chunk
    setCurrentStreamingMessageId(null);
    // Reset auto-scroll flag to resume auto-scrolling
    setResetAutoScroll((prev) => prev + 1);

    try {
      await startStream(trimmedInput, {
        chatId: chatId || undefined,
        onChunk: appendChunkToMessage,
        onComplete: handleStreamComplete,
        onError: handleStreamError,
      });
    } catch (error) {
      console.error("[ChatWindowContainer] Failed to start stream:", error);
      handleStreamError({ error: error as Error });
    }
  }, [appendChunkToMessage, inputValue, isStreaming, chatId, startStream, handleStreamComplete, handleStreamError]);

  useEffect(() => {
    return () => {
      cancelStream();
    };
  }, [cancelStream]);

  if (chatId && loading) {
    return (
      <View style={styles.container}>
        <ChatHeader />
        <View style={styles.content}>
          <View style={styles.messagesArea}>
            <View style={styles.loadingContainer}>{/* Loading indicator could go here */}</View>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ChatHeader />
      <View style={styles.content}>
        <View style={styles.messagesArea}>
          <MessagesContainer
            messages={messages}
            isStreaming={isStreaming}
            currentStreamingMessageId={currentStreamingMessageId}
            resetAutoScroll={resetAutoScroll}
          />
          <ChatInput
            value={inputValue}
            onChangeText={setInputValue}
            onSubmit={handleSubmit}
            disabled={isStreaming}
            placeholder="Type a message..."
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
  },
  messagesArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default ChatWindowContainer;
